import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssets, useDeleteAsset, useUpdateAsset } from '../lib/api/assets';
import { useCampaigns } from '../lib/api/campaigns';
import {
  ASSET_STATUSES,
  ASSET_TYPES_BY_CATEGORY,
  AssetStatus,
  AssetType,
  CampaignAsset,
} from '../lib/types';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { Icon, IconName } from '../components/ui/Icon';
import { Chip } from '../components/ui/Chip';
import { today, toISODate, fmtShort, daysFromToday, parseDate } from '../lib/dates';
import CreateAssetModal from '../components/modals/CreateAssetModal';
import { FilterPopover, FilterGroup } from '../components/ui/FilterPopover';
import AssetGanttTimeline from '../components/gantt/AssetGanttTimeline';
import { assetDisplayTitle } from '../lib/assetTitle';

const SOCIAL_TYPES = ASSET_TYPES_BY_CATEGORY['Social Media'];

type ViewMode = 'calendar' | 'timeline';

const VIEWS: { key: ViewMode; label: string; icon: IconName }[] = [
  { key: 'calendar', label: 'Calendar', icon: 'calendar' },
  { key: 'timeline', label: 'Timeline', icon: 'trending' },
];

const typeColor = (t: string) =>
  t === 'Promotion' ? 'var(--sitewide)' : t === 'Brand' ? 'var(--brand)' : 'var(--seasonal)';

const iconForAssetType = (t: AssetType): IconName => (t === 'Facebook Post' ? 'facebook' : 'instagram');

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const heatmapBg = (count: number) => {
  if (count === 0) return 'rgba(232, 163, 62, 0.18)';
  if (count === 1) return 'rgba(43, 138, 90, 0.12)';
  if (count === 2) return 'rgba(43, 138, 90, 0.25)';
  return 'rgba(43, 138, 90, 0.45)';
};

const buildMonthGrid = (year: number, month: number) => {
  const first = new Date(year, month, 1);
  const dow = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - dow);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
    if (i >= 34 && d.getMonth() !== month && d.getDay() === 0) break;
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1];
    const d = new Date(last);
    d.setDate(last.getDate() + 1);
    cells.push(d);
  }
  return cells;
};

const Social = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: posts = [], isLoading } = useAssets({ category: 'Social Media' });
  const { data: campaigns = [] } = useCampaigns();
  const deleteAsset = useDeleteAsset();
  const updateAsset = useUpdateAsset();
  const campaignById = new Map(campaigns.map((c) => [c.id, c]));
  const [editTarget, setEditTarget] = useState<CampaignAsset | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CampaignAsset | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const onTrayDragStart = (e: React.DragEvent, asset: CampaignAsset) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(asset.id));
    setDraggingId(asset.id);
  };
  const onTrayDragEnd = () => {
    setDraggingId(null);
    setDropTarget(null);
  };
  const onCellDragOver = (e: React.DragEvent, iso: string) => {
    if (draggingId == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTarget !== iso) setDropTarget(iso);
  };
  const onCellDrop = (e: React.DragEvent, iso: string) => {
    e.preventDefault();
    const id = Number(e.dataTransfer.getData('text/plain')) || draggingId;
    setDraggingId(null);
    setDropTarget(null);
    if (!id) return;
    updateAsset.mutate({ id, patch: { publish_date: iso } });
  };

  const [cursor, setCursor] = useState<Date>(() => {
    const t = today();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [statusFilter, setStatusFilter] = useState<AssetStatus | null>(null);
  const [campaignFilter, setCampaignFilter] = useState<string | null>(null);
  // Multiple types can be active at once; empty array = all types.
  const [typeFilter, setTypeFilter] = useState<AssetType[]>([]);
  const [heatmap, setHeatmap] = useState(true);
  const [view, setView] = useState<ViewMode>('calendar');
  const [createOpen, setCreateOpen] = useState(false);
  const [createDate, setCreateDate] = useState<string | undefined>();

  const filtered = useMemo(
    () =>
      posts.filter((p) => {
        if (statusFilter && p.status !== statusFilter) return false;
        if (campaignFilter && p.campaign !== campaignFilter) return false;
        if (typeFilter.length > 0 && !typeFilter.includes(p.asset_type)) return false;
        return true;
      }),
    [posts, statusFilter, campaignFilter, typeFilter],
  );

  // Undated posts ("To plan") don't appear on the calendar.
  const unscheduled = useMemo(
    () => filtered.filter((p) => !p.publish_date),
    [filtered],
  );

  // Expand multi-day **Instagram Giveaways** across every day in their
  // range. Other social asset types stay single-day.
  const expandToDays = (p: CampaignAsset): string[] => {
    if (!p.publish_date) return [];
    const single = [p.publish_date];
    if (p.asset_type !== 'Instagram Giveaway' || !p.end_date || p.end_date === p.publish_date) {
      return single;
    }
    const days: string[] = [];
    const start = parseDate(p.publish_date);
    const end = parseDate(p.end_date);
    const cursor = new Date(start);
    while (cursor <= end) {
      days.push(toISODate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  };

  const postsByDate = useMemo(() => {
    const m = new Map<string, CampaignAsset[]>();
    for (const p of filtered) {
      for (const iso of expandToDays(p)) {
        const arr = m.get(iso) ?? [];
        arr.push(p);
        m.set(iso, arr);
      }
    }
    for (const [, arr] of m) {
      arr.sort((a, b) => (a.publish_time ?? '').localeCompare(b.publish_time ?? ''));
    }
    return m;
  }, [filtered]);

  const grid = useMemo(() => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const weekCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (let i = 0; i < grid.length; i += 7) {
      // Dedupe by id so a multi-day giveaway only counts once per week.
      const seen = new Set<number>();
      for (let j = 0; j < 7; j++) {
        const d = grid[i + j];
        for (const p of postsByDate.get(toISODate(d)) ?? []) seen.add(p.id);
      }
      counts.set(i / 7, seen.size);
    }
    return counts;
  }, [grid, postsByDate]);

  const gaps = useMemo(() => {
    if (!heatmap) return [] as { weekStart: Date; weekEnd: Date }[];
    const out: { weekStart: Date; weekEnd: Date }[] = [];
    for (let i = 0; i < grid.length; i += 7) {
      if ((weekCounts.get(i / 7) ?? 0) !== 0) continue;
      const s = grid[i];
      const e = grid[i + 6];
      if (e.getMonth() !== cursor.getMonth() && s.getMonth() !== cursor.getMonth()) continue;
      out.push({ weekStart: s, weekEnd: e });
    }
    return out;
  }, [heatmap, grid, weekCounts, cursor]);

  const upcoming = useMemo(
    () =>
      filtered
        .filter((p): p is CampaignAsset & { publish_date: string } => {
          if (!p.publish_date) return false;
          const d = daysFromToday(p.publish_date);
          return d >= 0 && d <= 14;
        })
        .sort((a, b) => a.publish_date.localeCompare(b.publish_date)),
    [filtered],
  );

  const todayDate = today();
  const monthLabel = cursor.toLocaleDateString('en', { month: 'long', year: 'numeric' });
  // Show active or upcoming campaigns (still relevant — not already past).
  // Undated campaigns also count as "still relevant" — they haven't ended.
  const todayISOStr = toISODate(todayDate);
  const activeCampaigns = campaigns.filter((c) => !c.end_date || c.end_date >= todayISOStr).slice(0, 5);

  const openCreate = (date?: string) => {
    setCreateDate(date);
    setCreateOpen(true);
  };

  const summary = (p: CampaignAsset) => assetDisplayTitle(p, { withCampaign: true });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Planner</div>
          <h1 className="page-title">Social calendar</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {view === 'calendar' && (
            <Button
              variant={heatmap ? 'primary' : 'ghost'}
              onClick={() => setHeatmap((v) => !v)}
            >
              <Icon name="sparkle" size={13} /> Heatmap {heatmap ? 'on' : 'off'}
            </Button>
          )}
          <Button variant="primary" onClick={() => openCreate()}>
            <Icon name="plus" size={13} /> New post
          </Button>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 6 }}>
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            className={`filter-pill ${view === v.key ? 'active' : ''}`}
            onClick={() => setView(v.key)}
          >
            <Icon name={v.icon} size={12} />
            {v.label}
          </button>
        ))}
      </div>

      {view === 'calendar' && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, marginBottom: 10 }}>
        <button type="button" className="icon-btn" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
          <Icon name="chevronLeft" size={14} />
        </button>
        <div className="serif" style={{ fontSize: 24 }}>{monthLabel}</div>
        <button type="button" className="icon-btn" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
          <Icon name="chevronRight" size={14} />
        </button>
        <Button variant="ghost" onClick={() => setCursor(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1))}>
          Today
        </Button>
      </div>
      )}

      <div className="filter-bar" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className={`filter-pill ${statusFilter === null ? 'active' : ''}`} onClick={() => setStatusFilter(null)}>
            All
          </button>
          {ASSET_STATUSES.map((s) => (
            <button key={s} type="button" className={`filter-pill ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
              {s}
            </button>
          ))}
        </div>
        <FilterPopover activeCount={(campaignFilter ? 1 : 0) + (typeFilter.length > 0 ? 1 : 0)}>
          <FilterGroup label="Campaign">
            <button type="button" className={`filter-pill ${campaignFilter === null ? 'active' : ''}`} onClick={() => setCampaignFilter(null)}>
              All
            </button>
            {activeCampaigns.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`filter-pill ${campaignFilter === c.id ? 'active' : ''}`}
                onClick={() => setCampaignFilter(c.id)}
              >
                <span className="dot" style={{ background: typeColor(c.type) }} />
                {c.name.length > 24 ? c.name.slice(0, 24) + '…' : c.name}
              </button>
            ))}
          </FilterGroup>
          <FilterGroup label="Type">
            <button type="button" className={`filter-pill ${typeFilter.length === 0 ? 'active' : ''}`} onClick={() => setTypeFilter([])}>
              All
            </button>
            {SOCIAL_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                className={`filter-pill ${typeFilter.includes(t) ? 'active' : ''}`}
                onClick={() => setTypeFilter((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]))}
              >
                <Icon name={iconForAssetType(t)} size={12} />
                {t.replace('Instagram ', '').replace('Facebook ', 'FB ')}
              </button>
            ))}
          </FilterGroup>
        </FilterPopover>
      </div>

      {isLoading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>Loading…</div>
      ) : view === 'timeline' ? (
        filtered.length === 0 ? (
          <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
            No social posts match your filters.
          </div>
        ) : (
          <AssetGanttTimeline
            assets={filtered}
            onAssetClick={(a) => setEditTarget(a)}
            titleFor={(a) => assetDisplayTitle(a, { withCampaign: true })}
            colorFor={() => 'var(--brand)'}
            labelHeader="Social post"
            rangeWeeks={16}
            startOffsetWeeks={-2}
          />
        )
      ) : (
        <div className="cal">
          {DOW.map((d) => <div key={d} className="cal-dow">{d}</div>)}
          {grid.map((d, i) => {
            const iso = toISODate(d);
            const outside = d.getMonth() !== cursor.getMonth();
            const isToday = toISODate(todayDate) === iso;
            const cellPosts = postsByDate.get(iso) ?? [];
            const weekIndex = Math.floor(i / 7);
            const weekCount = weekCounts.get(weekIndex) ?? 0;
            const isSunday = i % 7 === 6;
            const isDropping = dropTarget === iso;
            return (
              <div
                key={iso}
                className={`cal-cell ${outside ? 'outside' : ''} ${isToday ? 'today' : ''}`}
                onClick={() => openCreate(iso)}
                onDragOver={(e) => onCellDragOver(e, iso)}
                onDragLeave={() => { if (dropTarget === iso) setDropTarget(null); }}
                onDrop={(e) => onCellDrop(e, iso)}
                style={{
                  cursor: 'pointer',
                  background: isDropping
                    ? '#fff3c2'
                    : heatmap && !outside ? heatmapBg(weekCount) : undefined,
                  outline: isDropping ? '2px solid #d4a300' : undefined,
                  outlineOffset: -2,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="cal-date">{d.getDate()}</div>
                  {heatmap && isSunday && !outside && (
                    <div style={{ fontSize: 10, color: 'var(--ink-4)', fontWeight: 500 }}>
                      {weekCount === 0 ? 'Gap' : `${weekCount}/wk`}
                    </div>
                  )}
                </div>
                {cellPosts.map((p) => {
                  const c = p.campaign ? campaignById.get(p.campaign) : null;
                  // Continuation day of a multi-day giveaway? Style it
                  // lighter so the start day still reads as the anchor.
                  const isMultiDay = p.asset_type === 'Instagram Giveaway'
                    && !!p.end_date && p.end_date !== p.publish_date;
                  const isContinuation = isMultiDay && p.publish_date !== iso;
                  return (
                    <div
                      key={`${p.id}-${iso}`}
                      className="cal-post"
                      style={{
                        borderLeftColor: typeColor(c?.type || 'Theme'),
                        opacity: isContinuation ? 0.65 : 1,
                        fontStyle: isContinuation ? 'italic' : 'normal',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditTarget(p);
                      }}
                      title={`${p.asset_type}${isMultiDay ? ` · ${fmtShort(p.publish_date as string)} → ${fmtShort(p.end_date as string)}` : (p.publish_time ? ` · ${p.publish_time}` : '')} · ${c?.name ?? ''} — ${summary(p)}`}
                    >
                      <Icon name={iconForAssetType(p.asset_type)} size={11} />
                      {p.publish_time && (
                        <span className="cal-post-time">{p.publish_time.slice(0, 5)}</span>
                      )}
                      <span className="cal-post-caption">{summary(p)}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {unscheduled.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <div className="section-title">To plan</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                {unscheduled.length} post{unscheduled.length === 1 ? '' : 's'} without a date — drag onto a day, or click to open.
              </div>
            </div>
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden', borderTop: '3px solid #f5d27a' }}>
            {unscheduled.map((p) => {
              const c = p.campaign ? campaignById.get(p.campaign) : null;
              const isDragging = draggingId === p.id;
              return (
                <div
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  draggable
                  onDragStart={(e) => onTrayDragStart(e, p)}
                  onDragEnd={onTrayDragEnd}
                  onClick={() => setEditTarget(p)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setEditTarget(p); }}
                  style={{
                    display: 'flex', gap: 12, alignItems: 'center',
                    padding: '10px 14px', width: '100%',
                    background: 'transparent',
                    borderBottom: '1px solid var(--border)',
                    textAlign: 'left', cursor: 'grab', fontSize: 13,
                    opacity: isDragging ? 0.4 : 1,
                  }}
                  title="Drag onto a day on the calendar — or click to open."
                >
                  <Icon name={p.asset_type === 'Facebook Post' ? 'facebook' : 'instagram'} size={13} />
                  <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a87800', fontWeight: 600 }}>
                    To plan
                  </span>
                  <span style={{ fontWeight: 500 }}>{assetDisplayTitle(p)}</span>
                  {c && <span style={{ color: 'var(--ink-4)' }}>· {c.name}</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-4)' }}>Drag or click →</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === 'calendar' && heatmap && gaps.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <div className="section-title">Gap weeks</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                {gaps.length} empty week{gaps.length === 1 ? '' : 's'} visible
              </div>
            </div>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
            {gaps.map((g, i) => (
              <div
                key={i}
                style={{
                  border: '1px dashed var(--status-review)',
                  background: 'rgba(251, 239, 212, 0.6)',
                  borderRadius: 'var(--r-md)',
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--status-review)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 6 }}>
                  Week of {fmtShort(g.weekStart)} — {fmtShort(g.weekEnd)}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 10 }}>
                  No posts planned. Fill with a quick teaser or restock note.
                </div>
                <Button variant="ghost" onClick={() => openCreate(toISODate(g.weekStart))}>
                  <Icon name="plus" size={12} /> Plan a post
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="section">
        <div className="section-head">
          <div>
            <div className="section-title">Next 14 days</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
              {upcoming.length} post{upcoming.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>
        {upcoming.length === 0 ? (
          <div className="card" style={{ padding: 20, color: 'var(--ink-4)' }}>
            No posts scheduled in the next two weeks.
          </div>
        ) : (
          <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}
          >
            {upcoming.map((p) => {
              const c = p.campaign ? campaignById.get(p.campaign) : null;
              return (
                <div key={p.id} className="post-card" style={{ position: 'relative' }}>
                  <div onClick={() => setEditTarget(p)} style={{ cursor: 'pointer' }}>
                    <div className="post-card-header">
                      <Icon name={iconForAssetType(p.asset_type)} size={13} />
                      <span className="mono">
                        {fmtShort(p.publish_date)}
                        {p.publish_time ? ` · ${p.publish_time.slice(0, 5)}` : ''}
                      </span>
                      <Chip kind="status" value={p.status} />
                    </div>
                    <div className="post-card-caption">{summary(p)}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                      {c ? (
                        <button
                          type="button"
                          className="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/campaigns/${c.id}`);
                          }}
                        >
                          {c.name}
                        </button>
                      ) : p.brand_details && p.brand_details.length > 0 ? (
                        p.brand_details.map((b) => b.name).join(', ')
                      ) : 'Unassigned'} · {p.asset_type}
                    </div>
                  </div>
                  <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                    <button
                      type="button"
                      className="icon-btn"
                      title="Delete post"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(p);
                      }}
                      style={{ color: 'var(--warn)' }}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CreateAssetModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultCampaignId={campaignFilter ?? undefined}
        defaultDate={createDate}
        defaultCategory="Social Media"
        defaultType={typeFilter[0] ?? 'Instagram Post'}
      />
      <CreateAssetModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        asset={editTarget}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteAsset.mutateAsync(deleteTarget.id);
            toast.push('Post deleted');
          } catch {
            toast.push('Could not delete post');
          }
          setDeleteTarget(null);
        }}
        title={deleteTarget ? `Delete "${summary(deleteTarget)}"?` : ''}
        description="This will permanently remove the post."
        confirmLabel="Delete"
        danger
        busy={deleteAsset.isPending}
      />
    </div>
  );
};

export default Social;
